import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Chip,
  Tooltip,
  FormControlLabel,
  Switch,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import {
  formatCurrency,
  getFrequencyLabel,
  getCategoryLabel,
  getCategoryColor,
} from "../../utils/formatters";
import ConstantCostDialog from "./ConstantCostDialog";

const INITIAL_FORM = {
  name: "",
  description: "",
  amount: "",
  frequency: "monthly",
  category: "",
  start_date: "",
  end_date: "",
};

const ConstantCostsTab = ({
  constantCosts,
  isMobile,
  showInactive,
  setShowInactive,
  onSaveConstantCost,
  onDeleteConstantCost,
  onActivateConstantCost,
}) => {
  const [dialog, setDialog] = useState({ open: false, mode: "add", costId: null });
  const [form, setForm] = useState(INITIAL_FORM);

  const openAddDialog = () => {
    setForm(INITIAL_FORM);
    setDialog({ open: true, mode: "add", costId: null });
  };

  const openEditDialog = (cost) => {
    setForm({
      name: cost.name || "",
      description: cost.description || "",
      amount: cost.amount?.toString() || "",
      frequency: cost.frequency || "monthly",
      category: cost.category || "",
      start_date: cost.start_date ? cost.start_date.split("T")[0] : "",
      end_date: cost.end_date ? cost.end_date.split("T")[0] : "",
    });
    setDialog({ open: true, mode: "edit", costId: cost.id });
  };

  const closeDialog = () => {
    setDialog({ open: false, mode: "add", costId: null });
    setForm(INITIAL_FORM);
  };

  const handleSave = async () => {
    const success = await onSaveConstantCost(form, dialog.mode, dialog.costId);
    if (success) {
      closeDialog();
    }
    return success;
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
          Zarządzaj stałymi kosztami działalności (czynsz, media, ubezpieczenia,
          itp.)
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                size="small"
              />
            }
            label="Pokaż nieaktywne"
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openAddDialog}
          >
            Dodaj koszt
          </Button>
        </Box>
      </Box>
      <TableContainer>
        <Table size={isMobile ? "small" : "medium"}>
          <TableHead>
            <TableRow>
              <TableCell>Nazwa</TableCell>
              <TableCell>Kategoria</TableCell>
              <TableCell>Częstotliwość</TableCell>
              <TableCell align="right">Kwota</TableCell>
              <TableCell align="right">Miesięcznie</TableCell>
              <TableCell align="center" sx={{ width: 120 }}>
                Akcje
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {constantCosts.map((cost) => (
              <TableRow
                key={cost.id}
                hover
                sx={{ opacity: cost.is_active ? 1 : 0.5 }}
              >
                <TableCell>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {cost.name}
                      {!cost.is_active && (
                        <Chip
                          label="Nieaktywny"
                          size="small"
                          color="default"
                          sx={{ ml: 1 }}
                        />
                      )}
                    </Typography>
                    {cost.description && (
                      <Typography variant="caption" color="text.secondary">
                        {cost.description}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={getCategoryLabel(cost.category)}
                    size="small"
                    color={getCategoryColor(cost.category)}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {getFrequencyLabel(cost.frequency)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formatCurrency(cost.amount)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography
                    variant="body2"
                    color="primary.main"
                    sx={{ fontWeight: 500 }}
                  >
                    {formatCurrency(cost.monthly_equivalent)}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      gap: 0.5,
                    }}
                  >
                    <Tooltip title="Edytuj">
                      <IconButton
                        size="small"
                        onClick={() => openEditDialog(cost)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {cost.is_active ? (
                      <Tooltip title="Dezaktywuj">
                        <IconButton
                          size="small"
                          color="warning"
                          onClick={() => onDeleteConstantCost(cost.id, false)}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Aktywuj">
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => onActivateConstantCost(cost.id)}
                        >
                          <CheckIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Usuń trwale">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => onDeleteConstantCost(cost.id, true)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {constantCosts.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ py: 3 }}
                  >
                    Brak kosztów stałych. Kliknij "Dodaj koszt" aby dodać
                    pierwszy.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <ConstantCostDialog
        open={dialog.open}
        mode={dialog.mode}
        form={form}
        setForm={setForm}
        onClose={closeDialog}
        onSave={handleSave}
      />
    </Box>
  );
};

export default ConstantCostsTab;
